import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import InviteLinkButton, { useInviteText } from '../components/InviteLinkButton';
import LeaveTeamCard, { useTeamText } from '../components/LeaveTeamCard';
import VatSettingsCard from '../components/VatSettingsCard';
import FeeSettingsCard from '../components/FeeSettingsCard';
import MetaAdsCard from '../components/MetaAdsCard';
import ApiKeysCard from '../components/ApiKeysCard';
import AccountCard from '../components/AccountCard';
import DeleteAccountCard from '../components/DeleteAccountCard';

const PLAN_LIMITS = { starter: 1, pro: 3 };
const PLATFORM_LABELS = { shopify: 'Shopify', woocommerce: 'WooCommerce', tiktok: 'TikTok Shop' };

function Settings({ token, onAuthError, onTenantChange }) {
  const { t, i18n } = useTranslation();
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({ name: '', default_currency: '', default_language: '', default_country: '', notification_email: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const inviteText = useInviteText();
  const teamText = useTeamText();
  const [inviting, setInviting] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamMessage, setTeamMessage] = useState('');
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [newConnection, setNewConnection] = useState({ platform: 'shopify', store_name: '', webhook_secret: '' });
  const [connectionError, setConnectionError] = useState('');
  const [savingConnection, setSavingConnection] = useState(false);

  const [slackStatus, setSlackStatus] = useState(null);
  const [loadingSlack, setLoadingSlack] = useState(true);
  const [connectingSlack, setConnectingSlack] = useState(false);
  const [slackError, setSlackError] = useState('');

  const [connectingTikTok, setConnectingTikTok] = useState(false);
  const [tiktokError, setTiktokError] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchTenant();
    fetchConnections();
    fetchSlackStatus();

    const params = new URLSearchParams(window.location.search);
    if (params.get('slack_error')) {
      setSlackError(t('settings.slackConnectError'));
    }
    if (params.get('slack_connected') || params.get('slack_error')) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (params.get('tiktok_error')) {
      setTiktokError(t('settings.tiktokConnectError'));
    }
    if (params.get('tiktok_connected') || params.get('tiktok_error')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTenant = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/tenant`, authHeaders);
      const data = response.data.data;
      setTenant(data);
      if (data.role === 'admin') { fetchTeam(); fetchDeletionRequests(); }
      setForm({
        name: data.name || '',
        default_currency: data.default_currency || 'EUR',
        default_language: data.default_language || 'es',
        default_country: data.default_country || 'ES',
        notification_email: data.notification_email || '',
      });
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('settings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setResendingVerification(true);
    setVerificationMessage('');
    try {
      await axios.post(`${API_URL}/api/v1/auth/resend-verification`, {}, authHeaders);
      setVerificationMessage(t('settings.verificationResent'));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setVerificationMessage(t('settings.verificationResendError'));
    } finally {
      setResendingVerification(false);
    }
  };

  const fetchTeam = async () => {
    setLoadingTeam(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/team`, authHeaders);
      setTeamMembers(response.data.data.members || []);
      setPendingInvites(response.data.data.pending_invites || []);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
    } finally {
      setLoadingTeam(false);
    }
  };

  const inviteMember = async (e) => {
    e.preventDefault();
    setInviting(true);
    setTeamError('');
    setTeamMessage('');
    try {
      const inviteResponse = await axios.post(`${API_URL}/api/v1/team/invite`, { email: inviteEmail, role: inviteRole }, authHeaders);
      setTeamMessage(inviteResponse.data && inviteResponse.data.email_sent ? inviteText.sent : inviteText.created);
      setInviteEmail('');
      setInviteRole('member');
      fetchTeam();
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else if (err.response && err.response.data && err.response.data.detail) setTeamError(err.response.data.detail);
      else setTeamError(t('settings.inviteError'));
    } finally {
      setInviting(false);
    }
  };

  const cancelInvite = async (inviteId, email) => {
    if (!window.confirm(inviteText.confirmCancel.replace('{{email}}', email || ''))) return;
    try {
      await axios.delete(`${API_URL}/api/v1/team/invites/${inviteId}`, authHeaders);
      setPendingInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else if (err.response && err.response.data && err.response.data.detail) setTeamError(err.response.data.detail);
      else setTeamError(inviteText.cancelError);
    }
  };

  const removeMember = async (memberId, email) => {
    if (!window.confirm(teamText.confirmRemove.replace('{{email}}', email || ''))) return;
    try {
      await axios.delete(`${API_URL}/api/v1/team/${memberId}`, authHeaders);
      setTeamMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else if (err.response && err.response.data && err.response.data.detail) setTeamError(err.response.data.detail);
      else setTeamError(t('settings.removeError'));
    }
  };

  const fetchDeletionRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/deletion-requests`, authHeaders);
      setDeletionRequests(response.data.data || []);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
    } finally {
      setLoadingRequests(false);
    }
  };

  const approveRequest = async (requestId) => {
    try {
      await axios.post(`${API_URL}/api/v1/deletion-requests/${requestId}/approve`, {}, authHeaders);
      setDeletionRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setTeamError(t('settings.approveError'));
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await axios.post(`${API_URL}/api/v1/deletion-requests/${requestId}/reject`, {}, authHeaders);
      setDeletionRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setTeamError(t('settings.rejectError'));
    }
  };

  const fetchConnections = async () => {
    setLoadingConnections(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/connections`, authHeaders);
      setConnections(response.data.data || []);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
    } finally {
      setLoadingConnections(false);
    }
  };

  const fetchSlackStatus = async () => {
    setLoadingSlack(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/integrations/slack`, authHeaders);
      setSlackStatus(response.data.data);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
    } finally {
      setLoadingSlack(false);
    }
  };

  const isTenantAdmin = !!(tenant && tenant.role === 'admin');

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isTenantAdmin) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const response = await axios.patch(`${API_URL}/api/v1/tenant`, form, authHeaders);
      setTenant((prev) => ({ ...prev, ...response.data.data }));
      if (onTenantChange) onTenantChange(response.data.data);
      if (form.default_language !== i18n.language) {
        i18n.changeLanguage(form.default_language);
        localStorage.setItem('omniflow_language', form.default_language);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const createConnection = async (e) => {
    e.preventDefault();
    setSavingConnection(true);
    setConnectionError('');
    try {
      const response = await axios.post(`${API_URL}/api/v1/connections`, newConnection, authHeaders);
      setConnections((prev) => [...prev, response.data.data]);
      setShowConnectForm(false);
      setNewConnection({ platform: 'shopify', store_name: '', webhook_secret: '' });
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else if (err.response && err.response.data && err.response.data.detail) {
        setConnectionError(err.response.data.detail);
      } else {
        setConnectionError(t('settings.connectionError'));
      }
    } finally {
      setSavingConnection(false);
    }
  };

  const deleteConnection = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/v1/connections/${id}`, authHeaders);
      setConnections((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setConnectionError(t('settings.connectionDeleteError'));
    }
  };

  const connectSlack = async () => {
    setConnectingSlack(true);
    setSlackError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/integrations/slack/authorize`, authHeaders);
      window.location.href = response.data.data.url;
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setSlackError(t('settings.slackConnectError'));
      setConnectingSlack(false);
    }
  };

  const connectTikTok = async () => {
    setConnectingTikTok(true);
    setTiktokError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/integrations/tiktok/authorize`, authHeaders);
      window.location.href = response.data.data.url;
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setTiktokError(t('settings.tiktokConnectError'));
      setConnectingTikTok(false);
    }
  };

  const disconnectSlack = async () => {
    try {
      await axios.delete(`${API_URL}/api/v1/integrations/slack`, authHeaders);
      setSlackStatus({ connected: false });
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setSlackError(t('settings.slackDisconnectError'));
    }
  };

  const inputClass =
    'w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent';

  const planType = tenant?.plan_type || 'starter';
  const connectionLimit = PLAN_LIMITS[planType] ?? 1;
  const atLimit = connections.length >= connectionLimit;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-white text-2xl font-bold">{t('settings.title')}</h2>
        <p className="text-muted text-sm mt-1">{t('settings.subtitle')}</p>
      </div>

      {tenant && !tenant.email_verified && (
        <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-amber-400 text-sm">{t('settings.emailNotVerified')}</p>
          <button
            onClick={resendVerification}
            disabled={resendingVerification}
            className="bg-transparent border border-amber-400 text-amber-400 text-xs px-3 py-1.5 rounded cursor-pointer shrink-0"
          >
            {resendingVerification ? t('orders.saving') : t('settings.resendVerification')}
          </button>
        </div>
      )}
      {verificationMessage && (
        <p className="text-muted text-xs mb-4 -mt-4">{verificationMessage}</p>
      )}

      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
        {loading ? (
          <p className="text-muted text-center py-8">{t('dashboard.loading')}</p>
        ) : (
          <form onSubmit={handleSave} className="flex flex-col gap-4">
{!isTenantAdmin && <p className="text-amber-400 text-xs">{teamText.adminOnly}</p>}
<fieldset disabled={!isTenantAdmin} className="contents">
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('settings.businessName')}</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-muted text-xs block mb-1.5">{t('settings.businessCountry')}</label>
              <select
                value={form.default_country}
                onChange={(e) => setForm((v) => ({ ...v, default_country: e.target.value }))}
                className={inputClass}
              >
                <option value="ES">{t('settings.countryES')}</option>
                <option value="FR">{t('settings.countryFR')}</option>
                <option value="US">{t('settings.countryUS')}</option>
                <option value="CA">{t('settings.countryCA')}</option>
                <option value="MX">{t('settings.countryMX')}</option>
                <option value="AR">{t('settings.countryAR')}</option>
                <option value="CL">{t('settings.countryCL')}</option>
                <option value="CO">{t('settings.countryCO')}</option>
                <option value="HT">{t('settings.countryHT')}</option>
              </select>
              <p className="text-muted text-xs mt-1.5">{t('settings.countryHint')}</p>
            </div>

            <div>
              <label className="text-muted text-xs block mb-1.5">{t('settings.currencyDefault')}</label>
              <select
                value={form.default_currency}
                onChange={(e) => setForm((v) => ({ ...v, default_currency: e.target.value }))}
                className={inputClass}
              >
                <option value="EUR">{t('settings.currencyEUR')}</option>
                <option value="USD">{t('settings.currencyUSD')}</option>
                <option value="CAD">{t('settings.currencyCAD')}</option>
                <option value="MXN">{t('settings.currencyMXN')}</option>
                <option value="ARS">{t('settings.currencyARS')}</option>
                <option value="CLP">{t('settings.currencyCLP')}</option>
                <option value="COP">{t('settings.currencyCOP')}</option>
                <option value="HTG">{t('settings.currencyHTG')}</option>
              </select>
              <p className="text-muted text-xs mt-1.5">{t('settings.currencyHint')}</p>
            </div>

            <div>
              <label className="text-muted text-xs block mb-1.5">{t('settings.languageDefault')}</label>
              <select
                value={form.default_language}
                onChange={(e) => setForm((v) => ({ ...v, default_language: e.target.value }))}
                className={inputClass}
              >
                <option value="es">{t('settings.languageEs')}</option>
                <option value="en">{t('settings.languageEn')}</option>
                <option value="fr">{t('settings.languageFr')}</option>
              </select>
            </div>

            <div>
              <label className="text-muted text-xs block mb-1.5">{t('settings.notificationEmail')}</label>
              <input
                type="email"
                value={form.notification_email}
                onChange={(e) => setForm((v) => ({ ...v, notification_email: e.target.value }))}
                className={inputClass}
              />
              <p className="text-muted text-xs mt-1.5">{t('settings.notificationEmailHint')}</p>
            </div>

            <div className="pt-2 flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className={`bg-accent text-panel font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer ${saving ? 'opacity-70' : ''}`}
              >
                {saving ? t('settings.saving') : t('settings.save')}
              </button>
              {saved && <span className="text-accent text-sm">{t('settings.saved')}</span>}
            </div>

            {tenant && (
              <p className="text-muted text-xs pt-2 border-t border-edge">
                {t('settings.currentPlan')}: <span className="text-white capitalize">{tenant.plan_type}</span>
              </p>
            )}
          </fieldset>
</form>
        )}
      </div>

      <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
        <p className="text-white text-sm font-semibold mb-1">{t('settings.slackIntegration')}</p>
        <p className="text-muted text-xs mb-4">{t('settings.slackIntegrationHint')}</p>

        {slackError && (
          <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-4">
            {slackError}
          </div>
        )}

        {loadingSlack ? (
          <p className="text-muted text-sm py-2">{t('dashboard.loading')}</p>
        ) : slackStatus && slackStatus.connected ? (
          <div className="bg-surfaceAlt border border-edge rounded-lg p-3 flex items-center justify-between gap-3">
            <p className="text-white text-sm">
              {t('settings.slackConnected', { team: slackStatus.team_name, channel: slackStatus.channel_name })}
            </p>
            <button
              onClick={disconnectSlack}
              className="text-red-400 text-xs px-2 py-1 rounded border border-[#7f1d1d] cursor-pointer shrink-0"
            >
              {t('settings.delete')}
            </button>
          </div>
        ) : (
          <>
            <p className="text-muted text-sm mb-3">{t('settings.slackNotConnected')}</p>
            <button
              onClick={connectSlack}
              disabled={connectingSlack}
              className={`bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer ${connectingSlack ? 'opacity-70' : ''}`}
            >
              {connectingSlack ? t('settings.connecting') : t('settings.connectSlack')}
            </button>
          </>
        )}
      </div>

      <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
        <p className="text-white text-sm font-semibold mb-1">TikTok Shop</p>
        <p className="text-muted text-xs mb-4">{t('settings.tiktokIntegrationHint')}</p>

        {tiktokError && (
          <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-4">
            {tiktokError}
          </div>
        )}

        <button
          onClick={connectTikTok}
          disabled={connectingTikTok}
          className={`bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer ${connectingTikTok ? 'opacity-70' : ''}`}
        >
          {connectingTikTok ? t('settings.connecting') : t('settings.connectTikTok')}
        </button>
      </div>

      <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg">
        <div className="flex items-center justify-between mb-1">
          <p className="text-white text-sm font-semibold">{t('settings.connectedStores')}</p>
          <span className="text-muted text-xs">{connections.length} / {connectionLimit}</span>
        </div>
        <p className="text-muted text-xs mb-4">
          {t('settings.connectedStoresHint')}
        </p>

        {connectionError && (
          <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-4">
            {connectionError}
          </div>
        )}

        {loadingConnections ? (
          <p className="text-muted text-sm py-4">{t('settings.loadingConnections')}</p>
        ) : connections.length === 0 ? (
          <p className="text-muted text-sm py-4">{t('settings.noConnectionsYet')}</p>
        ) : (
          <div className="flex flex-col gap-3 mb-4">
            {connections.map((conn) => (
              <div key={conn.id} className="bg-surfaceAlt border border-edge rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white text-sm font-semibold">
                    {PLATFORM_LABELS[conn.platform]} {conn.store_name ? `\u2014 ${conn.store_name}` : ''}
                  </p>
                  <button
                    onClick={() => deleteConnection(conn.id)}
                    className="text-red-400 text-xs px-2 py-1 rounded border border-[#7f1d1d] cursor-pointer"
                  >
                    {t('settings.delete')}
                  </button>
                </div>
                {conn.platform !== 'tiktok' && (
                  <>
                <p className="text-muted text-xs mb-1">{t('settings.webhookUrlHint', { platform: PLATFORM_LABELS[conn.platform] })}</p>
                <p className="text-accent text-xs font-mono break-all">
                  {API_URL}/webhooks/{conn.platform}/{conn.id}
                </p>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {atLimit && !showConnectForm && (
          <p className="text-muted text-xs">
            {t('settings.atLimit', { count: connectionLimit })}
          </p>
        )}

        {!atLimit && (
          <button
            onClick={() => setShowConnectForm((v) => !v)}
            className="bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer"
          >
            {showConnectForm ? t('settings.cancel') : t('settings.connectStore')}
          </button>
        )}

        {showConnectForm && (
          <form onSubmit={createConnection} className="flex flex-col gap-3 mt-4">
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('settings.platform')}</label>
              <select
                value={newConnection.platform}
                onChange={(e) => setNewConnection((v) => ({ ...v, platform: e.target.value }))}
                className={inputClass}
              >
                <option value="shopify">Shopify</option>
                <option value="woocommerce">WooCommerce</option>
              </select>
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('settings.storeName')}</label>
              <input
                type="text"
                value={newConnection.store_name}
                onChange={(e) => setNewConnection((v) => ({ ...v, store_name: e.target.value }))}
                className={inputClass}
                placeholder={t('settings.storeNamePlaceholder')}
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('settings.webhookSecret')}</label>
              <input
                type="text"
                name="webhook-signing-key"
                autoComplete="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                style={{ WebkitTextSecurity: 'disc' }}
                required
                value={newConnection.webhook_secret}
                onChange={(e) => setNewConnection((v) => ({ ...v, webhook_secret: e.target.value }))}
                className={inputClass}
                placeholder={t('settings.webhookSecretPlaceholder')}
              />
            </div>
            <button
              type="submit"
              disabled={savingConnection}
              className={`bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer self-start ${savingConnection ? 'opacity-70' : ''}`}
            >
              {savingConnection ? t('settings.connecting') : t('settings.connect')}
            </button>
          </form>
        )}
      </div>

      {tenant && tenant.role === 'admin' && (
        <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
          <p className="text-white text-sm font-semibold mb-1">{t('settings.teamTitle')}</p>
          <p className="text-muted text-xs mb-4">{t('settings.teamHint')}</p>

          {teamError && (
            <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-4">
              {teamError}
            </div>
          )}
          {teamMessage && (
            <p className="text-accent text-xs mb-4">{teamMessage}</p>
          )}

          <form onSubmit={inviteMember} className="flex flex-col sm:flex-row gap-2 mb-5">
            <input
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t('settings.inviteEmailPlaceholder')}
              className="flex-1 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
            >
              <option value="member">{t('settings.roleMember')}</option>
              <option value="admin">{t('settings.roleAdmin')}</option>
            </select>
            <button
              type="submit"
              disabled={inviting}
              className={`bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer ${inviting ? 'opacity-70' : ''}`}
            >
              {inviting ? t('settings.connecting') : t('settings.inviteButton')}
            </button>
          </form>

          {loadingTeam ? (
            <p className="text-muted text-sm py-2">{t('dashboard.loading')}</p>
          ) : (
            <>
              <p className="text-muted text-xs font-semibold mb-2">{t('settings.teamMembersLabel')}</p>
              <div className="flex flex-col gap-2 mb-4">
                {teamMembers.map((m) => (
                  <div key={m.id} className="bg-surfaceAlt border border-edge rounded-lg p-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-white text-sm">{m.email}</p>
                      <p className="text-muted text-xs capitalize">{m.role === 'admin' ? t('settings.roleAdmin') : t('settings.roleMember')}</p>
                    </div>
                    {m.is_owner || m.is_self ? (
                      <span className="text-muted text-xs shrink-0">{m.is_owner ? teamText.owner : teamText.you}</span>
                    ) : (
                      <button
                        onClick={() => removeMember(m.id, m.email)}
                        className="text-red-400 text-xs px-2 py-1 rounded border border-[#7f1d1d] cursor-pointer shrink-0"
                      >
                        {t('settings.removeMember')}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {pendingInvites.length > 0 && (
                <>
                  <p className="text-muted text-xs font-semibold mb-2">{t('settings.pendingInvitesLabel')}</p>
                  <p className="text-muted text-xs mb-2">{inviteText.hint}</p>
                  <div className="flex flex-col gap-2">
                    {pendingInvites.map((inv) => (
                      <div key={inv.id} className="bg-surfaceAlt border border-edge rounded-lg p-3">
                        <p className="text-white text-sm">{inv.email}</p>
                        <p className="text-muted text-xs capitalize">{inv.role === 'admin' ? t('settings.roleAdmin') : t('settings.roleMember')}</p>
                        {new Date(inv.expires_at) < new Date() ? (
                          <p className="text-amber-400 text-xs mt-2">{inviteText.expired}</p>
                        ) : (
                          <InviteLinkButton url={inv.invite_url} />
                        )}
                        <button
                          type="button"
                          onClick={() => cancelInvite(inv.id, inv.email)}
                          className="text-red-400 text-xs px-3 py-1.5 rounded border border-[#7f1d1d] cursor-pointer mt-2"
                        >
                          {inviteText.cancelInvite}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <AccountCard token={token} onAuthError={onAuthError} />

      {tenant && tenant.role === 'admin' && (
        <VatSettingsCard country={form.default_country} token={token} tenant={tenant} onAuthError={onAuthError} />
      )}

      {tenant && tenant.role === 'admin' && (
        <FeeSettingsCard token={token} onAuthError={onAuthError} currency={tenant.default_currency} />
      )}

      {tenant && tenant.role === 'admin' && (
        <MetaAdsCard token={token} onAuthError={onAuthError} />
      )}

      {tenant && tenant.role === 'admin' && (
        <ApiKeysCard token={token} onAuthError={onAuthError} />
      )}

      {tenant && tenant.is_owner === false && (
        <LeaveTeamCard token={token} onLeft={onAuthError} />
      )}

      {tenant && tenant.is_owner === true && (
        <DeleteAccountCard token={token} onDeleted={onAuthError} />
      )}

      {tenant && tenant.role === 'admin' && deletionRequests.length > 0 && (
        <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
          <p className="text-white text-sm font-semibold mb-1">{t('settings.deletionRequestsTitle')}</p>
          <p className="text-muted text-xs mb-4">{t('settings.deletionRequestsHint')}</p>
          {loadingRequests ? (
            <p className="text-muted text-sm py-2">{t('dashboard.loading')}</p>
          ) : (
            <div className="flex flex-col gap-2">
              {deletionRequests.map((r) => (
                <div key={r.id} className="bg-surfaceAlt border border-edge rounded-lg p-3">
                  <p className="text-white text-sm">{r.resource_label || r.resource_id}</p>
                  <p className="text-muted text-xs mb-2">
                    {r.resource_type} {'\u00b7'} {t('settings.requestedBy')}: {r.requester_email}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveRequest(r.id)}
                      className="bg-accent text-panel text-xs font-bold px-3 py-1.5 rounded cursor-pointer"
                    >
                      {t('settings.approve')}
                    </button>
                    <button
                      onClick={() => rejectRequest(r.id)}
                      className="text-red-400 text-xs px-3 py-1.5 rounded border border-[#7f1d1d] cursor-pointer"
                    >
                      {t('settings.reject')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Settings;





